import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useGlobalSettings } from '@/context/GlobalSettingsContext';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import {
  Shield, Sparkles, Globe, Lock, Linkedin, Facebook, Instagram, Youtube,
  Mail, CheckCircle, MapPin, ChevronRight,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useTranslation } from '@/hooks/useTranslation';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function TwitterIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

function TikTokIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.88-2.88 2.89 2.89 0 012.88-2.88c.28 0 .56.04.82.11v-3.49a6.37 6.37 0 00-.82-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.34-6.34V8.74a8.17 8.17 0 004.76 1.52V6.81a4.83 4.83 0 01-1-.12z" />
    </svg>
  );
}

const TRUST_BADGES = [
  { icon: Sparkles, label: 'Editorially Screened', color: 'text-violet-400' },
  { icon: Shield, label: 'Safety Reviewed', color: 'text-teal-400' },
  { icon: MapPin, label: 'Region-Aware Earnings', color: 'text-blue-400' },
  { icon: Lock, label: 'Privacy Protected', color: 'text-amber-400' },
];

const BRAND_LINKS = [
  { label: 'About Hustle-HubAI', to: '/about' },
  { label: 'How It Works', to: '/how-it-works' },
  { label: 'Contact Us', to: '/contact' },
];

const EXPLORE_LINKS = [
  { label: 'Browse Opportunities', to: '/browse' },
  { label: 'Trending Hustles', to: '/trending' },
  { label: 'New Opportunities', to: '/new' },
  { label: 'Top Rated', to: '/top-rated' },
  { label: 'Quick Match', to: '/match' },
  { label: 'AI Matching', to: '/profile-builder' },
  { label: 'Launch Your Hustle', to: '/launch-your-hustle' },
  { label: 'UK Self-Employment Guide', to: '/uk/self-employment' },
  { label: 'UK Start Your Business', to: '/uk/start-your-business' },
  { label: 'US Self-Employment Guide', to: '/us/self-employment' },
  { label: 'US Start Your Business', to: '/us/start-your-business' },
  { label: 'Japan Self-Employment Guide', to: '/japan/self-employment' },
  { label: 'Singapore Self-Employment Guide', to: '/singapore/self-employment' },
  { label: 'Hong Kong Self-Employment Guide', to: '/hong-kong/self-employment' },
  { label: 'Side Hustles for Retirees', to: '/side-hustles-for-retirees' },
  { label: 'Tools & Resources for Hustlers', to: '/tools-and-resources' },
];

const EDITORIAL_GUIDE_LINKS = [
  { label: 'All Editorial Guides', to: '/best' },
  { label: 'Best Side Hustle Apps UK', to: '/best/side-hustle-apps-uk' },
  { label: 'Best Apps to Make Extra Money UK', to: '/best/apps-to-make-extra-money-uk' },
  { label: 'Best Survey Sites UK', to: '/best/survey-sites-uk' },
  { label: 'Best Delivery Apps UK', to: '/best/delivery-apps-uk' },
  { label: 'Best Cashback Apps UK', to: '/best/cashback-apps-uk' },
  { label: 'Best Work-From-Home Side Hustles UK', to: '/best/work-from-home-side-hustles-uk' },
  { label: 'Side Hustles for Students UK', to: '/best/side-hustles-for-students-uk' },
  { label: 'Best Freelance Websites for Beginners', to: '/best/freelance-websites-for-beginners' },
];

const TRUST_LINKS = [
  { label: 'Trust & Safety', to: '/trust-safety' },
  { label: 'Editorial Standards', to: '/editorial-standards' },
  { label: 'How We Evaluate', to: '/how-we-evaluate' },
  { label: 'How We Rank', to: '/how-we-rank' },
  { label: 'Trust Score Explained', to: '/trust-score-explained' },
  { label: 'Editorial Policy', to: '/editorial-policy' },
  { label: 'Affiliate Disclosure', to: '/affiliate-disclosure' },
  { label: 'Earnings Disclaimer', to: '/earnings-disclaimer' },
  { label: 'Sponsored Placement', to: '/sponsored-policy' },
  { label: 'Partner with Us', to: '/partner-with-us' },
  { label: 'Report a Listing', to: '/trust-safety#reporting' },
  { label: 'Contact Us', to: '/contact' },
];

const LEGAL_LINKS = [
  { label: 'Privacy Policy', to: '/privacy-policy' },
  { label: 'Terms of Service', to: '/terms' },
  { label: 'Cookie Policy', to: '/cookie-policy' },
  { label: 'Earnings Disclaimer', to: '/earnings-disclaimer' },
  { label: 'Affiliate Disclosure', to: '/affiliate-disclosure' },
  { label: 'Accessibility', to: '/contact#accessibility' },
];

const SOCIALS = [
  { icon: Linkedin, label: 'LinkedIn', href: '#' },
  { icon: TwitterIcon, label: 'Twitter/X', href: '#' },
  { icon: Facebook, label: 'Facebook', href: '#' },
  { icon: Instagram, label: 'Instagram', href: '#' },
  { icon: Youtube, label: 'YouTube', href: '#' },
  { icon: TikTokIcon, label: 'TikTok', href: '#' },
];

const POPULAR_CATEGORIES = [
  'Freelancing', 'Content Creation', 'Remote Work', 'Passive Income',
  'Teaching', 'Gig Work', 'AI & Digital Work', 'Investments',
];

function FooterLink({ to, label, soon }) {
  return (
    <li>
      <Link
        to={soon ? '#' : to}
        className={`text-[13px] leading-relaxed text-slate-400 hover:text-white transition-colors duration-200 inline-flex items-center gap-1.5 py-1 ${soon ? 'pointer-events-none opacity-60' : ''}`}
        tabIndex={soon ? -1 : 0}
        data-testid={`footer-link-${label.toLowerCase().replace(/[\s/&]+/g, '-')}`}
      >
        {label}
        {soon && <span className="text-[9px] font-semibold text-slate-500 bg-slate-800 px-1.5 py-0.5 rounded">Soon</span>}
      </Link>
    </li>
  );
}

function SectionHead({ children }) {
  return (
    <h4 className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-500 mb-5" style={{ fontFamily: 'Outfit, sans-serif' }}>
      {children}
    </h4>
  );
}

function GlobalSelector({ label, value, onValueChange, items, placeholder, testId }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.15em] text-slate-600 font-semibold mb-1.5 block">{label}</label>
      <Select value={value} onValueChange={onValueChange}>
        <SelectTrigger
          className="bg-white/[0.03] border-white/[0.07] text-slate-300 h-9 text-[13px] rounded-lg hover:bg-white/[0.05] hover:border-white/[0.12] focus:ring-teal-500/20 transition-all"
          data-testid={testId}
        >
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent className="max-h-60">
          {items.map(item => (
            <SelectItem key={item.value} value={item.value}>{item.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

export default function Footer() {
  const { language, setLanguage, currency, setCurrency, country, setCountry, LANGUAGES, CURRENCIES, COUNTRIES } = useGlobalSettings();
  const t = useTranslation();
  const [email, setEmail] = useState('');
  const [subscribing, setSubscribing] = useState(false);
  const [subscribed, setSubscribed] = useState(false);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubscribing(true);
    try {
      await axios.post(`${API}/newsletter/subscribe`, { email, interests: [] });
      setSubscribed(true);
      toast.success('Subscribed to Weekly Hustle Digest!');
    } catch {
      toast.error('Failed to subscribe. Try again.');
    } finally {
      setSubscribing(false);
    }
  };

  const langItems = LANGUAGES.map(l => ({ value: l.code, label: l.label }));
  const currItems = CURRENCIES.map(c => ({ value: c.code, label: c.label }));
  const countryItems = COUNTRIES.map(c => ({ value: c, label: c }));

  return (
    <footer className="bg-slate-950 text-slate-400 mt-auto" role="contentinfo" data-testid="footer">

      {/* ═══ TRUST BADGES STRIP ═══ */}
      <div className="border-b border-slate-800/50">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-5">
          <div className="flex items-center justify-center lg:justify-between gap-6 lg:gap-8 overflow-x-auto scrollbar-hide" data-testid="footer-trust-badges">
            {TRUST_BADGES.map(badge => (
              <div key={badge.label} className="flex items-center gap-2.5 flex-shrink-0" data-testid={`trust-badge-${badge.label.toLowerCase().replace(/\s+/g, '-')}`}>
                <badge.icon className={`h-4 w-4 ${badge.color}`} />
                <span className="text-[12px] sm:text-[13px] font-medium text-slate-400 whitespace-nowrap">{badge.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ═══ MAIN COLUMNS — DESKTOP (5 columns) ═══ */}
      <div className="hidden lg:block">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-16">
          <div className="grid grid-cols-12 gap-8">

            {/* Column 1: Brand — wider */}
            <div className="col-span-3">
              <div className="flex items-center gap-2.5 mb-4">
                <div className="w-10 h-10 flex-shrink-0">
                  <img src="/logo192.png" alt="HustleHub AI" className="w-full h-full object-contain" width="40" height="40" />
                </div>
                <span className="text-lg font-semibold text-white tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  HustleHub<span className="text-teal-400">.ai</span>
                </span>
              </div>
              <p className="text-[13px] leading-relaxed text-slate-500 mb-6 max-w-xs">
                Find smarter ways to earn — tailored to your time, tools, and location.
              </p>
              <ul className="space-y-2.5">
                {BRAND_LINKS.map(link => <FooterLink key={link.label} {...link} />)}
              </ul>

              {/* Editorial Guides — "Best of" comparison pages */}
              <div className="mt-7 pt-6 border-t border-slate-800/50">
                <SectionHead>Editorial Guides</SectionHead>
                <ul className="space-y-2.5">
                  {EDITORIAL_GUIDE_LINKS.map(link => <FooterLink key={link.label} {...link} />)}
                </ul>
              </div>
            </div>

            {/* Column 2: Explore */}
            <div className="col-span-2">
              <SectionHead>Explore</SectionHead>
              <ul className="space-y-2.5">
                {EXPLORE_LINKS.map(link => <FooterLink key={link.label} {...link} />)}
              </ul>
            </div>

            {/* Column 3: Trust & Safety */}
            <div className="col-span-2">
              <SectionHead>Trust & Safety</SectionHead>
              <ul className="space-y-2.5">
                {TRUST_LINKS.map(link => <FooterLink key={link.label} {...link} />)}
              </ul>
            </div>

            {/* Column 4: Legal & Policies */}
            <div className="col-span-2">
              <SectionHead>Legal & Policies</SectionHead>
              <ul className="space-y-2.5">
                {LEGAL_LINKS.map(link => <FooterLink key={link.label} {...link} />)}
              </ul>
            </div>

            {/* Column 5: Global Settings */}
            <div className="col-span-3">
              <SectionHead>Global Settings</SectionHead>
              <div className="space-y-4">
                <GlobalSelector label="Language" value={language} onValueChange={setLanguage} items={langItems} placeholder="Select language" testId="language-selector" />
                <GlobalSelector label="Currency" value={currency} onValueChange={setCurrency} items={currItems} placeholder="Select currency" testId="currency-selector" />
                <GlobalSelector label="Country / Region" value={country} onValueChange={setCountry} items={countryItems} placeholder="Select country" testId="country-selector" />
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* ═══ MAIN COLUMNS — MOBILE / TABLET ACCORDION ═══ */}
      <div className="lg:hidden">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 py-10">
          {/* Brand — always visible */}
          <div className="mb-6 pb-6 border-b border-slate-800/50">
            <div className="flex items-center gap-2.5 mb-3">
              <div className="w-10 h-10 flex-shrink-0">
                <img src="/logo192.png" alt="HustleHub AI" className="w-full h-full object-contain" width="40" height="40" />
              </div>
              <span className="text-lg font-semibold text-white tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
                HustleHub<span className="text-teal-400">.ai</span>
              </span>
            </div>
            <p className="text-[13px] leading-relaxed text-slate-500">
              Find smarter ways to earn — tailored to your time, tools, and location.
            </p>
          </div>

          <Accordion type="multiple" className="space-y-0">
            {[
              { title: 'Explore', links: EXPLORE_LINKS },
              { title: 'Editorial Guides', links: EDITORIAL_GUIDE_LINKS },
              { title: 'Trust & Safety', links: TRUST_LINKS },
              { title: 'Legal & Policies', links: LEGAL_LINKS },
            ].map(section => (
              <AccordionItem key={section.title} value={section.title} className="border-slate-800/50">
                <AccordionTrigger className="text-sm font-semibold text-slate-300 hover:text-white hover:no-underline py-4 [&>svg]:text-slate-600">
                  {section.title}
                </AccordionTrigger>
                <AccordionContent>
                  <ul className="space-y-3 pb-2">
                    {section.links.map(link => <FooterLink key={link.label} {...link} />)}
                  </ul>
                </AccordionContent>
              </AccordionItem>
            ))}

            <AccordionItem value="global-settings" className="border-slate-800/50">
              <AccordionTrigger className="text-sm font-semibold text-slate-300 hover:text-white hover:no-underline py-4 [&>svg]:text-slate-600">
                Global Settings
              </AccordionTrigger>
              <AccordionContent>
                <div className="space-y-4 pb-2">
                  <GlobalSelector label="Language" value={language} onValueChange={setLanguage} items={langItems} placeholder="Select language" testId="mobile-language-selector" />
                  <GlobalSelector label="Currency" value={currency} onValueChange={setCurrency} items={currItems} placeholder="Select currency" testId="mobile-currency-selector" />
                  <GlobalSelector label="Country / Region" value={country} onValueChange={setCountry} items={countryItems} placeholder="Select country" testId="mobile-country-selector" />
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          {/* Quick brand links */}
          <div className="mt-5 pt-5 border-t border-slate-800/50">
            <div className="flex flex-wrap gap-x-5 gap-y-2">
              {BRAND_LINKS.filter(l => !l.soon).map(link => (
                <Link key={link.label} to={link.to} className="text-[13px] text-slate-500 hover:text-white transition-colors">{link.label}</Link>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ═══ NEWSLETTER SIGNUP ═══ */}
      <div className="border-t border-slate-800/50">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-10">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
            <div className="flex items-start gap-4">
              <div className="hidden sm:flex items-center justify-center w-11 h-11 rounded-xl bg-teal-500/10 border border-teal-500/15 flex-shrink-0 mt-0.5">
                <Mail className="h-5 w-5 text-teal-400" />
              </div>
              <div>
                <h4 className="text-[15px] font-semibold text-white mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Weekly Hustle Digest
                </h4>
                <p className="text-[13px] text-slate-500">Get personalized earning ideas each week.</p>
              </div>
            </div>
            {subscribed ? (
              <div className="flex items-center gap-2 text-teal-400 text-sm font-medium" data-testid="newsletter-success">
                <CheckCircle className="h-4 w-4" />
                You're subscribed!
              </div>
            ) : (
              <form onSubmit={handleSubscribe} className="flex flex-col gap-2 w-full lg:w-auto" data-testid="newsletter-form">
                <div className="flex items-center gap-3 w-full lg:w-auto">
                  <div className="relative flex-1 lg:w-72">
                    <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-600" />
                    <input
                      type="email"
                      placeholder="Enter your email"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      required
                      className="w-full h-10 pl-10 pr-4 text-sm bg-white/[0.03] border border-white/[0.07] rounded-lg text-slate-200 placeholder:text-slate-600 focus:outline-none focus:ring-1 focus:ring-teal-500/30 focus:border-teal-500/20 transition-all"
                      data-testid="newsletter-email-input"
                      aria-label="Email for newsletter"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={subscribing}
                    className="h-10 px-6 bg-teal-500 hover:bg-teal-400 text-slate-900 font-semibold text-sm rounded-lg transition-all duration-200 disabled:opacity-50 flex-shrink-0"
                    data-testid="newsletter-subscribe-button"
                  >
                    {subscribing ? 'Subscribing...' : 'Subscribe'}
                  </button>
                </div>
                <p className="text-[11px] text-slate-600 leading-relaxed lg:max-w-[19rem]" data-testid="newsletter-privacy-microcopy">
                  We use your email to send the Weekly Hustle Digest. See our{' '}
                  <Link to="/privacy-policy" className="underline hover:text-slate-400">Privacy Policy</Link>. You can unsubscribe from emails at any time.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>

      {/* ═══ SOCIAL + SEO CATEGORIES ═══ */}
      <div className="border-t border-slate-800/50">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-7">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
            {/* Social Icons */}
            <div className="flex items-center gap-2.5 sm:gap-2.5" data-testid="social-links">
              {SOCIALS.map(social => (
                <a
                  key={social.label}
                  href={social.href}
                  aria-label={social.label}
                  className="w-10 h-10 sm:w-9 sm:h-9 rounded-lg bg-white/[0.03] border border-white/[0.06] flex items-center justify-center text-slate-500 hover:text-white hover:bg-white/[0.07] hover:border-white/[0.12] transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:ring-offset-2 focus:ring-offset-slate-950"
                  data-testid={`social-${social.label.toLowerCase().replace(/[\s/]+/g, '-')}`}
                >
                  <social.icon className="h-4 w-4" />
                </a>
              ))}
            </div>

            {/* Popular Categories (SEO) */}
            <div className="flex items-center gap-2 flex-wrap" data-testid="popular-categories">
              <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-600 mr-1">Popular:</span>
              {POPULAR_CATEGORIES.map((cat, i) => (
                <span key={cat} className="flex items-center">
                  <Link
                    to={`/browse?category=${encodeURIComponent(cat)}`}
                    className="text-[12px] text-slate-500 hover:text-white transition-colors duration-200"
                    data-testid={`popular-cat-${cat.toLowerCase().replace(/[\s&]+/g, '-')}`}
                  >
                    {cat}
                  </Link>
                  {i < POPULAR_CATEGORIES.length - 1 && <span className="text-slate-800 mx-1.5">·</span>}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ═══ COPYRIGHT BAR ═══ */}
      <div className="border-t border-slate-800/50 bg-slate-950/80">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-5">
          <div className="flex flex-col gap-3">
            {/* Trust/scale stats */}
            <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1.5 text-[11px] text-slate-500">
              <span className="flex items-center gap-1.5"><CheckCircle className="h-3 w-3 text-teal-500" /> 1,900+ opportunities reviewed</span>
              <span className="hidden sm:inline text-slate-700">·</span>
              <span className="flex items-center gap-1.5"><Globe className="h-3 w-3 text-teal-500" /> Coverage across 40+ countries</span>
              <span className="hidden sm:inline text-slate-700">·</span>
              <span className="flex items-center gap-1.5"><Shield className="h-3 w-3 text-teal-500" /> Editorially screened</span>
              <span className="hidden sm:inline text-slate-700">·</span>
              <a href="mailto:admin@hustle-hubai.com" className="flex items-center gap-1.5 hover:text-white transition-colors">
                <Mail className="h-3 w-3 text-teal-500" /> admin@hustle-hubai.com
              </a>
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-3 border-t border-slate-800/40">
              <p className="text-[11px] text-slate-500" data-testid="copyright-text">
                &copy; {new Date().getFullYear()} HustleHub AI. All rights reserved. HustleHub AI is an editorial comparison platform; we are not an employer or financial adviser.
              </p>
              <div className="flex items-center gap-4 flex-wrap justify-center">
                <Link to="/terms" className="text-[11px] text-slate-500 hover:text-white transition-colors" data-testid="copyright-terms-link">Terms</Link>
                <Link to="/privacy-policy" className="text-[11px] text-slate-500 hover:text-white transition-colors" data-testid="copyright-privacy-link">Privacy</Link>
                <Link to="/cookie-policy" className="text-[11px] text-slate-500 hover:text-white transition-colors" data-testid="copyright-cookies-link">Cookies</Link>
                <Link to="/affiliate-disclosure" className="text-[11px] text-slate-500 hover:text-white transition-colors" data-testid="copyright-affiliate-link">Affiliate Disclosure</Link>
                <Link to="/partner-with-us" className="text-[11px] text-slate-500 hover:text-white transition-colors" data-testid="copyright-partner-link">Partner with us</Link>
                <Link to="/editorial-standards" className="text-[11px] text-slate-500 hover:text-white transition-colors" data-testid="copyright-editorial-standards-link">Editorial Standards</Link>
              </div>
            </div>

            {/* Public operator identity block — visible on every page (Impact / AdSense readiness) */}
            <div className="pt-3 border-t border-slate-800/40">
              <p className="text-[11px] text-slate-500 leading-relaxed max-w-4xl" data-testid="operator-identity-block">
                HustleHub AI is <span className="text-slate-400">independently operated from the United Kingdom</span>. For editorial corrections, listing issues, partnership enquiries or privacy questions, contact{' '}
                <a href="mailto:admin@hustle-hubai.com" className="text-slate-400 hover:text-white underline underline-offset-2">admin@hustle-hubai.com</a>.{' '}
                Rankings are editorially independent — sponsored placements are clearly labelled and do not buy placement. Earnings vary by country, time, skill and platform terms and are <span className="text-slate-400">not guaranteed</span>. Treat &ldquo;earn while you sleep&rdquo;, &ldquo;passive income guaranteed&rdquo; and get-rich-quick claims as red flags.
              </p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
